import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * How many tabs of `widths` fit `available` px. When the active tab falls past the fold it
 * takes the last visible slot, so slots are dropped until it fits there too. The active tab
 * keeps a slot even when nothing fits (it is squeezed instead): the row has to say whose
 * book is open.
 */
export const fitTabs = (widths: number[], activeIndex: number, available: number): number => {
  const sum = (n: number) => widths.slice(0, n).reduce((total, w) => total + w, 0);
  let count = 0;
  while (count < widths.length && sum(count + 1) <= available) count += 1;
  if (activeIndex >= count) {
    // One slot more first: the active tab may fit after every tab that fitted.
    count += 1;
    while (count > 1 && sum(count - 1) + widths[activeIndex] > available) count -= 1;
  }
  return activeIndex >= 0 ? Math.max(count, 1) : count;
};

// A separator and the `gap-1` on each side of it: 1 + 4 + 4 px. Every tab after the first
// takes one, and so does the menu.
const TAB_SPACING_PX = 9;
// Kept in sync with the menus' `w-72`.
const MENU_WIDTH_PX = 288;

// Fractional: `offsetWidth` rounds, and rounded tab widths add up a pixel or two short of
// the real row, which the shrinkable tabs then absorb as an ellipsis on every one of them.
const widthOf = (el: Element): number => el.getBoundingClientRect().width;

/**
 * Fits a tab row that ends in a menu button. The caller renders, inside `rowRef`, two hidden
 * replica rows: `measureRef` with one replica per tab in order, and `menuMeasureRef` with the
 * menu button bare and with the widest "+N more" it can show. `layoutKey` is whatever, when
 * it changes, changes the tabs.
 */
export const useFitTabs = (activeIndex: number, tabCount: number, layoutKey: unknown, isLoading = false) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const menuMeasureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(tabCount);
  const [menuOpensLeft, setMenuOpensLeft] = useState(false);

  const calculateVisible = useCallback(() => {
    const row = rowRef.current;
    const measure = measureRef.current;
    const menuMeasure = menuMeasureRef.current;
    if (!row || !measure || !menuMeasure) return;
    const rowWidth = widthOf(row);
    // Not laid out (jsdom, a hidden panel): nothing to fit against, so hide nothing.
    if (rowWidth === 0) {
      setVisibleCount(measure.children.length);
      return;
    }
    const widths = Array.from(measure.children).map(
      (child, i) => widthOf(child) + (i > 0 ? TAB_SPACING_PX : 0),
    );
    // The menu's room comes from its replicas, never from the live button: "+N more" depends
    // on the fit, and refitting against it can flip between two counts ("+10" is wider than
    // "+11" in proportional figures) until React gives up.
    const [bareMenu, labelledMenu] = Array.from(menuMeasure.children).map(
      (child) => widthOf(child) + TAB_SPACING_PX,
    );
    const allTabs = widths.reduce((total, w) => total + w, 0);
    const count =
      allTabs <= rowWidth - bareMenu
        ? widths.length
        : fitTabs(widths, activeIndex, rowWidth - labelledMenu);
    setVisibleCount(count);
    // The menu drops down rightwards from the button unless that would run past the row.
    // An active tab past the fold stands in the last slot, in place of the one there (in
    // slot 0 it has no separator before it).
    const swapped = activeIndex >= count && count > 0;
    const tabsWidth =
      widths.slice(0, swapped ? count - 1 : count).reduce((total, w) => total + w, 0) +
      (swapped ? widths[activeIndex] - (count === 1 ? TAB_SPACING_PX : 0) : 0);
    setMenuOpensLeft(rowWidth - tabsWidth - TAB_SPACING_PX < MENU_WIDTH_PX);
  }, [activeIndex]);

  // The observer outlives `activeIndex` changes, so it calls whichever fit is current: one
  // bound to the old index would fit the new active tab's width against the old slot.
  const calculateRef = useRef(calculateVisible);
  useLayoutEffect(() => {
    calculateRef.current = calculateVisible;
  });

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const observer = new ResizeObserver(() => calculateRef.current());
    observer.observe(row);
    // The tabs' own widths change without the row's when the web font swaps in.
    if (measureRef.current) observer.observe(measureRef.current);
    return () => observer.disconnect();
  }, [isLoading]);

  // Layout effect: fit before paint, so the row never flashes every tab and then trims.
  // `isLoading`: the row only mounts once loading ends.
  useLayoutEffect(calculateVisible, [calculateVisible, layoutKey, isLoading]);

  return { rowRef, measureRef, menuMeasureRef, visibleCount, menuOpensLeft };
};

/** The tabs shown: the first `visibleCount`, the active one standing in the last slot when
 *  it falls past the fold. */
export const visibleTabsOf = <T>(tabs: T[], visibleCount: number, activeIndex: number): T[] => {
  const shown = tabs.slice(0, visibleCount);
  if (activeIndex >= visibleCount && shown.length > 0) shown[shown.length - 1] = tabs[activeIndex];
  return shown;
};
